import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, ShieldCheck } from "lucide-react";
import Hero from "../components/Hero";
import ProductCard from "../components/ProductCard";
import ProcessScroll from "../components/ProcessScroll";
import Reveal from "../components/Reveal";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../components/ui/accordion";
import { api } from "../lib/api";
import { toast } from "sonner";

const CDN = "https://customer-assets.emergentagent.com/job_peptide-marketplace-8/artifacts";

const whyCols = [
  { t: "Precision Manufacturing", d: "Synthesized under exacting conditions with rigorous in-process control." },
  { t: "Third-Party Verified", d: "Independent laboratories confirm identity, concentration and purity." },
  { t: "Transparent Documentation", d: "A certificate of analysis accompanies every single batch." },
  { t: "Exceptional Service", d: "Responsive research support and fast, discreet dispatch." },
];

const stats = [
  { value: 99, suffix: "%+", label: "Purity" },
  { value: 100, suffix: "%", label: "Batch Tested" },
  { value: 48, suffix: "h", label: "Average Dispatch" },
  { value: 12000, suffix: "+", label: "Research Orders" },
];

const journal = [
  { t: "Understanding Peptide Stability", tag: "Guide", img: `${CDN}/495gm8yn_GHK-Cu%20PDC-1-%20White.png` },
  { t: "How Certificates of Analysis Work", tag: "Documentation", img: `${CDN}/m2g3mnup_BPC-157%20PDC-1-%20White.png` },
  { t: "Best Storage Practices", tag: "Handling", img: `${CDN}/e38rr370_TB-500%20PDC-1-%20White.png` },
];

const testimonials = [
  {
    q: "The 5-Amino-1MQ arrived perfectly packaged and the COA matched independent HPLC. Consistent, high-purity work — I'll be reordering.",
    a: "Dr. M. Reyes", role: "University Research Lab", product: "5-Amino-1MQ", rating: 5,
    img: "https://customer-assets-7cd3h4nn.emergentagent.net/job_peptide-marketplace-8/artifacts/ip3o30o1_customer%20review.png",
  },
  {
    q: "Vial integrity, labeling, and the branded card feel next level. Dispatch was fast and documentation was flawless.",
    a: "A. Suzuki", role: "Independent Researcher", product: "5-Amino-1MQ", rating: 5,
    img: "https://customer-assets-7cd3h4nn.emergentagent.net/job_peptide-marketplace-8/artifacts/wckdnqez_customer%20review%201.png",
  },
  {
    q: "TB-500 is now a mainstay in our tissue-repair protocol. Batch-to-batch consistency has been outstanding.",
    a: "L. Chen", role: "Sports Science Lab", product: "TB-500", rating: 5,
    img: "https://customer-assets-7cd3h4nn.emergentagent.net/job_peptide-marketplace-8/artifacts/9gbdm45s_customer%203.png",
  },
  {
    q: "Melanotan II reconstituted cleanly and stored well. Cold-chain handling on arrival was noticeably better than other suppliers.",
    a: "T. Fischer", role: "Dermatological Research", product: "Melanotan II", rating: 5,
    img: "https://customer-assets-7cd3h4nn.emergentagent.net/job_peptide-marketplace-8/artifacts/n3ribs91_customer%204.png",
  },
  { q: "Our laboratory continues to reorder with confidence — consistency is everything.", a: "K. Patel", role: "Biotech Institute", product: "GHK-Cu", rating: 5 },
  { q: "The COA transparency and batch-level traceability set them apart.", a: "R. Alvarez", role: "Independent Bioanalyst", product: "Ipamorelin", rating: 4 },
];

const faqs = [
  { q: "What are research peptides?", a: "Short chains of amino acids synthesized for laboratory and scientific research, intended exclusively for in-vitro and research applications by qualified professionals." },
  { q: "Who can purchase?", a: "Academic researchers, laboratories, universities, research institutions and qualified professionals. Not for human or veterinary use." },
  { q: "How are products tested?", a: "Every batch undergoes independent third-party testing via HPLC and mass spectrometry. A Certificate of Analysis accompanies every order." },
  { q: "How should products be stored?", a: "Lyophilized peptides should be stored cold and protected from light. Follow cold-chain handling once reconstituted." },
  { q: "Where can I access COAs?", a: "Certificates of Analysis are available on each product page and in the Certificates section." },
];

function Counter({ to, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 1.8, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setVal(Math.floor(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

const Label = ({ children }) => (
  <p className="text-[11px] uppercase tracking-[0.3em] text-[#4ADE80] mb-6">{children}</p>
);

export default function Home() {
  const [products, setProducts] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [nlEmail, setNlEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Please fill all fields with valid info."); }
    finally { setSending(false); }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!nlEmail) return;
    try {
      const { data } = await api.post("/newsletter", { email: nlEmail });
      toast.success(data.message || "Subscribed.");
      setNlEmail("");
    } catch { toast.error("Please enter a valid email."); }
  };

  const featured = (products || []).slice(0, 4);
  const editorialImg = `${CDN}/l09ffqv8_Retatrutide%20PDC-1-%20White.png`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="bg-[#0F0F10]">
      <SEO
        title="Precision is Everything"
        description="EndaG1 — premium pharmaceutical-grade peptides. Independently verified, batch-tested with COA, and trusted by laboratories worldwide. Research use only."
        canonicalPath="/"
        jsonLd={[faqSchema]}
      />
      <Hero />

      {/* EDITORIAL SPLIT */}
      <section className="mx-auto max-w-[1500px] px-6 sm:px-10 py-24 md:py-36 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <Reveal className="order-2 lg:order-1">
          <Label>Our Standard</Label>
          <h2 className="font-serif-editorial text-5xl md:text-7xl text-[#F5F5F5] leading-[0.95]">Pharmaceutical Grade.<br />Nothing Less.</h2>
          <p className="mt-8 max-w-md text-lg text-[#A8A8A8] font-light leading-relaxed">
            Every EndaG1 compound is held to a single uncompromising standard — verified purity, documented provenance, and consistency batch after batch.
          </p>
          <Link to="/products" data-testid="editorial-cta" className="mt-10 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-[#4ADE80] link-underline">
            Learn More <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="order-1 lg:order-2">
          <div className="relative aspect-[4/5] overflow-hidden vial-halo flex items-center justify-center">
            <img src={editorialImg} alt="EndaG1 research vial" className="h-full w-auto object-contain vial-blend-soft" />
          </div>
        </Reveal>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-[#171717] border-y border-white/10 py-24 md:py-32">
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-16">
            <div><Label>The Collection</Label>
              <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight text-[#F5F5F5]">Featured Compounds</h2></div>
            <Link to="/products" data-testid="view-all-products-btn" className="btn-press inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] border border-white/20 text-[#F5F5F5] px-7 py-3.5 hover:border-[#4ADE80] hover:text-[#4ADE80]">
              View All <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
          {products === null ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton aspect-[3/4]" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* INTERACTIVE SCROLL PROCESS */}
      <ProcessScroll />

      {/* WHY ENDAG1 */}
      <section id="why" className="bg-[#171717] border-y border-white/10 py-24 md:py-32">
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10">
          <Reveal className="mb-16"><Label>Why EndaG1</Label>
            <h2 className="font-serif-editorial text-4xl md:text-6xl text-[#F5F5F5] max-w-2xl leading-[1.02]">A different standard of research supply.</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/[0.06]">
            {whyCols.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.08} className="bg-[#171717] p-8 md:p-10">
                <span className="font-display text-[#4ADE80] text-sm">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-6 font-display text-xl text-[#F5F5F5] tracking-tight">{w.t}</h3>
                <p className="mt-4 text-[15px] text-[#A8A8A8] font-light leading-relaxed">{w.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATISTICS */}
      <section className="mx-auto max-w-[1500px] px-6 sm:px-10 py-24 md:py-32">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          {stats.map((s) => (
            <Reveal key={s.label}>
              <p className="font-display font-semibold text-5xl md:text-6xl tracking-tighter text-[#F5F5F5]"><Counter to={s.value} suffix={s.suffix} /></p>
              <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#A8A8A8]">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* RESEARCH JOURNAL */}
      <section id="journal" className="bg-[#171717] border-y border-white/10 py-24 md:py-32">
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10">
          <Reveal className="mb-16"><Label>Insights</Label>
            <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight text-[#F5F5F5]">Research Journal</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {journal.map((b, i) => (
              <Reveal key={b.t} delay={i * 0.08}>
                <div data-testid={`journal-card-${i}`} className="group border border-white/[0.06] bg-[#0F0F10] overflow-hidden transition-[border-color] duration-500 hover:border-[#4ADE80]/40">
                  <div className="aspect-[16/11] overflow-hidden flex items-center justify-center">
                    <img src={b.img} alt={b.t} loading="lazy" className="h-full w-auto object-contain vial-blend-soft transition-transform duration-[900ms] group-hover:scale-105" />
                  </div>
                  <div className="p-8">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#4ADE80]">{b.tag}</span>
                    <h3 className="mt-3 font-serif-editorial text-2xl text-[#F5F5F5]">{b.t}</h3>
                    <span className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-[#A8A8A8]">Coming soon</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="mx-auto max-w-[1500px] px-6 sm:px-10 py-24 md:py-36">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <Label>Trusted By Researchers</Label>
            <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight text-[#F5F5F5]">Verified Reviews</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1" data-testid="reviews-summary-stars">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-5 w-5 fill-[#4ADE80] text-[#4ADE80]" />)}
            </div>
            <div className="text-[13px] text-[#A8A8A8]">
              <span className="text-[#F5F5F5] font-display">4.9</span> / 5 · <span className="text-[#F5F5F5]">1,240+</span> researchers
            </div>
          </div>
        </Reveal>
        <div data-testid="reviews-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testimonials.map((t, i) => (
            <Reveal key={t.a} delay={i * 0.05}>
              <article data-testid={`review-card-${i}`} className="h-full flex flex-col border border-white/[0.06] bg-[#171717] transition-[border-color] duration-500 hover:border-[#4ADE80]/40 overflow-hidden">
                {t.img && (
                  <div className="aspect-[4/3] overflow-hidden bg-[#0F0F10] relative">
                    <img
                      src={t.img}
                      alt={`Customer photo — ${t.product}`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[900ms] hover:scale-[1.04]"
                    />
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-[#0F0F10] bg-[#4ADE80] px-2 py-1 rounded-full font-medium">
                      <ShieldCheck className="h-3 w-3" /> Verified Buyer
                    </span>
                  </div>
                )}
                <div className="flex flex-col flex-1 p-7">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1" aria-label={`${t.rating} out of 5`}>
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star key={s} className={`h-3.5 w-3.5 ${s < t.rating ? "fill-[#4ADE80] text-[#4ADE80]" : "text-white/15"}`} />
                      ))}
                    </div>
                    {!t.img && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[#4ADE80]">
                        <ShieldCheck className="h-3.5 w-3.5" /> Verified
                      </span>
                    )}
                  </div>
                  <blockquote className={`mt-5 font-serif-editorial ${t.img ? "text-lg" : "text-xl"} text-[#F5F5F5] leading-snug flex-1`}>
                    “{t.q}”
                  </blockquote>
                  <footer className="mt-6 pt-5 border-t border-white/10">
                    <div className="text-[13px] text-[#F5F5F5]">{t.a}</div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-[#A8A8A8] mt-1">{t.role}</div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-[#4ADE80] mt-2">Purchased · {t.product}</div>
                  </footer>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#171717] border-y border-white/10 py-24 md:py-32">
        <div className="mx-auto max-w-[860px] px-6 sm:px-10">
          <Reveal className="mb-14"><Label>Questions</Label>
            <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight text-[#F5F5F5]">Frequently Asked</h2></Reveal>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-white/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg text-[#F5F5F5] hover:no-underline hover:text-[#4ADE80] py-6">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#A8A8A8] font-light leading-relaxed text-[15px]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="mx-auto max-w-[900px] px-6 sm:px-10 py-28 md:py-36 text-center">
        <h2 className="font-serif-editorial text-5xl md:text-7xl text-[#F5F5F5]">Stay Informed.</h2>
        <p className="mt-6 text-lg text-[#A8A8A8] font-light max-w-lg mx-auto">Product updates, research documentation, and scientific insight — delivered with restraint.</p>
        <form onSubmit={subscribe} data-testid="newsletter-form" className="mt-12 flex flex-col sm:flex-row gap-6 max-w-lg mx-auto items-center">
          <input data-testid="newsletter-email-input" type="email" required value={nlEmail} onChange={(e) => setNlEmail(e.target.value)} placeholder="you@laboratory.org"
            className="flex-1 w-full bg-transparent border-b border-white/20 px-1 py-3 text-center sm:text-left text-[#F5F5F5] outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a] font-light" />
          <button data-testid="newsletter-submit-btn" type="submit" className="btn-press bg-[#4ADE80] text-[#0F0F10] uppercase tracking-[0.18em] text-[12px] px-9 py-4 hover:bg-[#6FE998] whitespace-nowrap">Subscribe</button>
        </form>
      </section>

      {/* CONTACT / SUPPORT */}
      <section id="contact" className="bg-[#171717] border-t border-white/10 py-24 md:py-32">
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <Label>Support</Label>
            <h2 className="font-serif-editorial text-4xl md:text-6xl text-[#F5F5F5] leading-[1.02]">Speak with the lab.</h2>
            <p className="mt-8 text-lg text-[#A8A8A8] font-light max-w-md leading-relaxed">Questions on compounds, bulk orders, or documentation? Our research team responds within one business day.</p>
            <a href="mailto:info@endag1.com" className="mt-8 inline-block text-[13px] uppercase tracking-[0.18em] text-[#4ADE80] hover:text-[#6FE998] transition-colors">info@endag1.com</a>
          </Reveal>
          <Reveal delay={0.15}>
            <form onSubmit={submitContact} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input data-testid="contact-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border-b border-white/20 py-3 text-[#F5F5F5] outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a] font-light" />
                <input data-testid="contact-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-transparent border-b border-white/20 py-3 text-[#F5F5F5] outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a] font-light" />
              </div>
              <input data-testid="contact-subject" required placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-3 text-[#F5F5F5] outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a] font-light" />
              <textarea data-testid="contact-message" required rows={4} placeholder="Message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-3 text-[#F5F5F5] outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a] font-light resize-none" />
              <button data-testid="contact-submit-btn" type="submit" disabled={sending} className="btn-press bg-[#4ADE80] text-[#0F0F10] uppercase tracking-[0.18em] text-[12px] px-9 py-4 hover:bg-[#6FE998] disabled:opacity-50">{sending ? "Sending…" : "Send Message"}</button>
            </form>
          </Reveal>
        </div>
      </section>

      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
